import React, { useEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { AuthCookies } from "../enums/AuthCookies";
import { BenefitType } from "../enums/BenefitType";
import { useAuth } from "oidc-react";
import { useGuestAuth } from "../guest/GuestAuthProvider";
import { getSessionDetails, parseCookies } from '@pds/sessionTimer/sessionTimer.min'
import { DateTime } from "luxon";
import { PdsHeading, PdsLayoutContainer, PdsLink, PdsPrimaryNavigation, } from '@principal/design-system-react';
import { GlobalHeader } from "@principal/individual-header-component";
import { Environment } from "@principal/aws-types";
import { findEnterpriseIdFor } from "../../service/EnterpriseIdService";
import axios from "axios";

const Header = () => {
    const auth = useAuth();
    const { isLoggedIn: isGuestLoggedIn, logout: guestLogout } = useGuestAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const pathname = location.pathname;
    const benefit = Object.values(BenefitType).find(c => pathname.includes(c));
    useEffect(() => {
        if (!['/', '/error'].includes(pathname) && !benefit) {
            navigate('/');
        }
    }, [pathname, benefit, navigate]);
    const user = auth?.userData;
    const signedIn = !!user;
    const authLoading = auth?.isLoading;
    const guestFlowPage = pathname.endsWith('/login') || pathname.includes('/verify-email') || pathname.includes('/guest');
    const hasLogin = !pathname.includes("physician") && !guestFlowPage;
    const hasEnterpriseHeader = pathname.includes('employee') && signedIn && !guestFlowPage;
    const hasHeaderText = !guestFlowPage;

    const signIn = handleSignIn();
    const signOut = handleSignOut();

    useEffect(() => {
        function isSessionNotExpired() {
            const expirationCookie = parseCookies(document.cookie);
            const sessionDetails = getSessionDetails(expirationCookie);
            const systemTime = sessionDetails?.systemTime;
            if (!systemTime || typeof sessionDetails.systemTime !== 'number') {
                return false;
            }
            const systemTimeMS = sessionDetails.systemTime * 1000;
            const TEN_MINUTES_IN_MS = 600000;
            return systemTimeMS + TEN_MINUTES_IN_MS > DateTime.now().toMillis();
        }
        if (!signedIn && !authLoading && isSessionNotExpired()) {
            signIn()
        }
    }, [signedIn, authLoading, signIn]);

    useEffect(() => {
        if (!user) {
            return
        }
        axios.defaults.headers.common.Authorization = `Bearer ${user?.access_token}`
        const interceptor = axios.interceptors.request.use(config => {
            if (!config.url?.startsWith(process.env.REACT_APP_FORMS_SERVICE_URL!)) {
                config.headers.delete('Authorization')
            }
            return config;
        });
        return () => {
            axios.defaults.headers.common.Authorization = undefined
            axios.interceptors.response.eject(interceptor)
        };
    }, [user])

    return (
        <>
            <div style={{ position: 'relative' }}>
            {hasEnterpriseHeader
                ? <GlobalHeader eid={findEnterpriseIdFor(user)}
                    env={process.env.REACT_APP_ENVIRONMENT === 'prod' ? Environment.Prod : Environment.Staging}
                    getAccessToken={() => user?.access_token}
                    logger={console} />
                : <PdsPrimaryNavigation variant="inverted"
                    logoHref="#"
                    loginLink={isGuestLoggedIn ? "none" : (hasLogin ? (signedIn ? "logout" : "login") : "none")}
                    loginLinkVariant="primary-inverted"
                    loginLinkHref="#"
                    layoutContainerVariant="default"
                    layoutContainerRemovePadding="md"
                    onHandleClick={signedIn ? signOut : signIn} />}
            {isGuestLoggedIn && (
                <PdsLink
                    style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}
                    button="primary-inverted"
                    href="#"
                    ariaLabel="Exit"
                    onClick={(e: Event) => {
                        e.preventDefault();
                        guestLogout();
                    }}
                >
                    Exit
                </PdsLink>
            )}
            </div>
            {hasHeaderText &&
                <PdsLayoutContainer className="pds-u-padding-top-20">
                    <PdsHeading headingTag="h1">Claim submission</PdsHeading>
                </PdsLayoutContainer>}
        </>
    )

    function handleSignIn() {
        return () => {
            const currentLocation = window.location.href;
            if (signedIn || authLoading || guestFlowPage || currentLocation.includes("/redirect")) {
                return;
            }
            sessionStorage.setItem(AuthCookies.REDIRECT_URI, currentLocation)
            auth.signIn();
        };
    }

    function handleSignOut() {
        return () => {
            sessionStorage.removeItem(AuthCookies.REDIRECT_URI)
            auth.signOut();
        };
    }
};

export default Header;